import { GridArea } from './types/grid';
import { ObjectFit } from './types/style';
import { getGridAreaForPosition } from './validation/PositionMapping';
export interface PositionedContent {
    left: string;
    top: string;
    width: string;
    height: string;
    // Available dimensions for responsive sizing (in pixels at 1920x1080 base)
    availableWidth?: number;
    availableHeight?: number;
}

export type PaddingMode = 'container' | 'canvas';

// Maximum scale value (1 = full content area, maintains intuitive scaling)
export const MAX_SCALE = 1;

export enum Position {
    TopLeft = 'topLeft',
    TopCenter = 'topCenter',
    Top = 'topCenter',  // Alias for TopCenter
    TopRight = 'topRight',
    MiddleLeft = 'middleLeft',
    Left = 'middleLeft',  // Alias for MiddleLeft
    Center = 'center',
    MiddleRight = 'middleRight',
    Right = 'middleRight',  // Alias for MiddleRight
    BottomLeft = 'bottomLeft',
    BottomCenter = 'bottomCenter',
    Bottom = 'bottomCenter',  // Alias for BottomCenter
    BottomRight = 'bottomRight',
    
    // Video editing layout presets
    LeftThird = 'leftThird',        // Left 1/3 of screen, full height
    RightThird = 'rightThird',      // Right 1/3 of screen, full height
    TopBanner = 'topBanner',        // Full width, top 20%
    BottomBanner = 'bottomBanner',  // Full width, bottom 20%
    FullWidth = 'fullWidth',        // Full width, center height
    LeftPanel = 'leftPanel',        // Left 40%, full height
    RightPanel = 'rightPanel'       // Right 40%, full height
}

function calculatePositionOffset(
    position: Position,
    contentWidth: number,
    contentHeight: number,
    scaledWidth: number,
    scaledHeight: number
): { offsetX: number; offsetY: number } {
    const offsetX = (() => {
        switch (position) {
            case Position.TopLeft:
            case Position.MiddleLeft:
            case Position.BottomLeft:
                return 0;
            case Position.TopRight:
            case Position.MiddleRight:
            case Position.BottomRight:
                return contentWidth - scaledWidth;
            default: // Center, TopCenter, BottomCenter
                return (contentWidth - scaledWidth) / 2;
        }
    })();

    const offsetY = (() => {
        switch (position) {
            case Position.TopLeft:
            case Position.TopCenter:
            case Position.TopRight:
                return 0;
            case Position.BottomLeft:
            case Position.BottomCenter:
            case Position.BottomRight:
                return contentHeight - scaledHeight;
            default: // Center, MiddleLeft, MiddleRight
                return (contentHeight - scaledHeight) / 2;
        }
    })();

    return { offsetX, offsetY };
}

// Utility to resolve grid-based position and size into normalized coordinates
export function resolveGridArea(
    gridArea: GridArea
): { x: number; y: number; width: number; height: number } {
    const gridSize = gridArea.gridSize ?? 5; // Default to 5x5 if not specified
    const cellWidth = 1 / gridSize;
    const cellHeight = 1 / gridSize;
  
    // Convert from 1-indexed to 0-indexed grid positions
    const x = (gridArea.col - 1) * cellWidth;
    const y = (gridArea.row - 1) * cellHeight;
    const width = gridArea.cols * cellWidth;
    const height = gridArea.rows * cellHeight;
  
    const result = { x, y, width, height };
    return result;
}

export function calculatePositionedContent(
    area: { x: number; y: number; width: number; height: number },
    scale: number,
    layoutPadding: number | { top: number; bottom: number; left: number; right: number },
    paddingMode: PaddingMode = 'container',
    position: Position = Position.Center
): PositionedContent {
    
    // Ensure scale is valid
    if (typeof scale !== 'number' || isNaN(scale)) {
        console.warn('[Utils] Invalid scale value:', scale, 'defaulting to 1');
        scale = 1;
    }
    
    const containerWidth = area.width * 100;
    const containerHeight = area.height * 100;
    
    // Handle layoutPadding as either number or object
    let paddingValue: number;
    if (typeof layoutPadding === 'object' && layoutPadding !== null) {
        // If object, use average of all sides for now (or could use specific sides)
        paddingValue = (layoutPadding.top + layoutPadding.bottom + layoutPadding.left + layoutPadding.right) / 4;
    } else if (typeof layoutPadding === 'number') {
        paddingValue = layoutPadding;
    } else {
        console.warn('[Utils] Invalid layoutPadding:', layoutPadding, 'defaulting to 0');
        paddingValue = 0;
    }
    
    // Calculate padding based on mode
    const padding = paddingMode === 'container' 
        ? Math.min(containerWidth, containerHeight) * paddingValue
        : 100 * paddingValue; // Use full canvas width for padding
    
    // Calculate content area (container minus padding)
    const contentWidth = containerWidth - (padding * 2);
    const contentHeight = containerHeight - (padding * 2);
    
    // Cap scale to MAX_SCALE
    const cappedScale = Math.min(scale, MAX_SCALE);
    
    // Calculate scaled dimensions
    // Scale 1 = full content area, Scale 0.5 = half of content area, Scale 0.25 = quarter of content area
    const scaledWidth = contentWidth * (cappedScale / MAX_SCALE);
    const scaledHeight = contentHeight * (cappedScale / MAX_SCALE);
    
    
    // Calculate offset based on position
    const { offsetX, offsetY } = calculatePositionOffset(
        position,
        contentWidth,
        contentHeight,
        scaledWidth,
        scaledHeight
    );

    // Calculate available dimensions in pixels (assuming 1920x1080 base video)
    const baseVideoWidth = 1920;
    const baseVideoHeight = 1080;
    const availableWidthPx = (scaledWidth / 100) * baseVideoWidth;
    const availableHeightPx = (scaledHeight / 100) * baseVideoHeight;
    
    const result = {
        left: `${area.x * 100 + padding + offsetX}%`,
        top: `${area.y * 100 + padding + offsetY}%`,
        width: `${scaledWidth}%`,
        height: `${scaledHeight}%`,
        availableWidth: availableWidthPx,
        availableHeight: availableHeightPx
    };
    
    return result;
}

export function calculateGridPosition(
    gridArea: GridArea | undefined,
    scale: number,
    layoutPadding: number | { top: number; bottom: number; left: number; right: number },
    paddingMode: PaddingMode = 'container',
    position: Position = Position.Center
): PositionedContent {
    
    // If gridArea is undefined, convert position to gridArea
    const effectiveGridArea = gridArea || getGridAreaForPosition(position);
    
    if (!effectiveGridArea) {
        throw new Error(`Cannot resolve grid area: gridArea is undefined and position ${position} could not be mapped to grid area`);
    }
    
    const area = resolveGridArea(effectiveGridArea);
    return calculatePositionedContent(area, scale, layoutPadding, paddingMode, position);
}

export interface VideoDimensions {
    width: number;
    height: number;
}

// Utility function to get dimensions from different types of media sources
export function getVideoDimensions(frame: CanvasImageSource): VideoDimensions {
    if (frame instanceof HTMLVideoElement) {
        return { width: frame.videoWidth, height: frame.videoHeight };
    }
    if (frame instanceof HTMLImageElement) {
        return { width: frame.naturalWidth, height: frame.naturalHeight };
    }
    return { width: 0, height: 0 };
}

// Calculates the draw rectangle for an image given objectFit, image size, and container size
export function getObjectFitDrawRect(
    objectFit: ObjectFit | undefined,
    imgWidth: number,
    imgHeight: number,
    containerWidth: number,
    containerHeight: number
) {
    let drawWidth = containerWidth;
    let drawHeight = containerHeight;
    let drawX = 0;
    let drawY = 0;

    if (objectFit === 'scale-down') {
        const fitsNaturally = imgWidth <= containerWidth && imgHeight <= containerHeight;
        if (fitsNaturally) {
            drawWidth = imgWidth;
            drawHeight = imgHeight;
            drawX = (containerWidth - imgWidth) / 2;
            drawY = (containerHeight - imgHeight) / 2;
        } else {
            const scale = Math.min(containerWidth / imgWidth, containerHeight / imgHeight);
            drawWidth = imgWidth * scale;
            drawHeight = imgHeight * scale;
            drawX = (containerWidth - drawWidth) / 2;
            drawY = (containerHeight - drawHeight) / 2;
        }
    } else if (objectFit === 'contain') {
        const scale = Math.min(containerWidth / imgWidth, containerHeight / imgHeight);
        drawWidth = imgWidth * scale;
        drawHeight = imgHeight * scale;
        drawX = (containerWidth - drawWidth) / 2;
        drawY = (containerHeight - drawHeight) / 2;
    } else if (objectFit === 'cover') {
        const scale = Math.max(containerWidth / imgWidth, containerHeight / imgHeight);
        drawWidth = imgWidth * scale;
        drawHeight = imgHeight * scale;
        drawX = (containerWidth - drawWidth) / 2;
        drawY = (containerHeight - drawHeight) / 2;
    } else if (objectFit === 'none') {
        drawWidth = imgWidth;
        drawHeight = imgHeight;
        drawX = (containerWidth - imgWidth) / 2;
        drawY = (containerHeight - imgHeight) / 2;
    } else {
        // 'fill' or default: stretch to fill
        drawWidth = containerWidth;
        drawHeight = containerHeight;
        drawX = 0;
        drawY = 0;
    }
    return { drawX, drawY, drawWidth, drawHeight };
}